import React, {
  forwardRef,
  useEffect,
  useRef,
  useCallback,
  useState,
} from "react";
import { cn } from "@/shared/utils/shadcn";

interface AutoResizeTextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  minRows?: number;
  maxRows?: number;
}

const AutoResizeTextarea = forwardRef<
  HTMLTextAreaElement,
  AutoResizeTextareaProps
>(({ minRows = 1, maxRows = 5, className = "", disabled, ...props }, ref) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";
    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseInt(computedStyle.lineHeight);

    const currentRows = Math.floor(textarea.scrollHeight / lineHeight);
    const newRows = Math.min(Math.max(currentRows, minRows), maxRows);

    textarea.style.height = `${newRows * lineHeight}px`;
  }, [minRows, maxRows]);

  const handleInput = useCallback(() => {
    adjustTextareaHeight();
  }, [adjustTextareaHeight]);

  useEffect(() => {
    adjustTextareaHeight();
  }, [adjustTextareaHeight]);

  return (
    <textarea
      ref={(node) => {
        textareaRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      }}
      className={cn(
        "w-full p-4 rounded-md overflow-hidden transition-all duration-50 ease-in-out resize-y focus:outline-gray-600 focus:ring-none",
        disabled && "bg-gray-100 cursor-not-allowed",
        className
      )}
      onInput={handleInput}
      disabled={disabled}
      style={{
        minHeight: `${minRows * 1.5}em`,
        maxHeight: `${maxRows * 1.5}em`,
      }}
      {...props}
    />
  );
});

AutoResizeTextarea.displayName = "AutoResizeTextarea";

export default AutoResizeTextarea;
